import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ThemeProvider } from '@mui/material/styles';
import { Drawer, Box, List, Divider, ListItem, ListItemIcon, ListItemText, Typography, LinearProgress } from '@mui/material';
import { Dashboard, Storage, Code, Work, Message, Settings, Logout, Group, Help } from '@mui/icons-material';
import { styled } from '@mui/system';
import theme from '../Theme';
import './CSS/OrganizationDashboard.css';
import { API_URL } from '../apiConfig';

const drawerWidth = 240;

const SidebarContainer = styled(Drawer)(() => ({
  width: drawerWidth,
  '& .MuiDrawer-paper': {
    width: drawerWidth,
    boxSizing: 'border-box',
  },
}));

const LogoText = styled(Typography)(() => ({
  fontFamily: 'Questrial, sans-serif',
  fontSize: '1.5rem',
  fontWeight: 'bold',
}));

const StorageUsageContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  textAlign: 'center',
}));

const menuItems = [
  { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' },
  { text: 'Datasets', icon: <Storage />, path: '/datasets' },
  { text: 'AI Models', icon: <Code />, path: '/ai-models' },
  { text: 'Projects', icon: <Work />, path: '/projects' },
  { text: 'Communications', icon: <Message />, path: '/communications' },
  { text: 'Organization', icon: <Group />, path: '/organization' },
  { text: 'FAQ', icon: <Help />, path: '/faq' },
];

const Sidebar = ({ handleLogout }) => {
  const [storageUsage, setStorageUsage] = useState(0);
  const [storageLimit, setStorageLimit] = useState(0);
  const [emailVerified, setEmailVerified] = useState(false);

  useEffect(() => {
    const fetchStorageData = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        const { data } = await axios.get(`${API_URL}/api/storage_usage`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        });
        setStorageUsage(parseFloat(data.used_storage) || 0);
        setStorageLimit(parseFloat(data.storage_limit) || 0);
        setEmailVerified(data.email_verified); // Update the email verification state
      } catch (error) {
        console.error('Error fetching storage data:', error);
      }
    };
    fetchStorageData();
  }, []);

  const storagePercentage = storageLimit ? (storageUsage / storageLimit) * 100 : 0;

  return (
    <ThemeProvider theme={theme}>
      <SidebarContainer variant="permanent" anchor="left">
        <Box p={2} display="flex" alignItems="center" justifyContent="center">
          <LogoText>archiveME.ai</LogoText>
        </Box>
        <Divider />
        <List>
          {menuItems.map((item) => (
            <ListItem button component={Link} to={item.path} key={item.text}>
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItem>
          ))}
        </List>
        <List sx={{ marginTop: 'auto' }}>
          {emailVerified && ( // Conditionally render the storage usage section
            <StorageUsageContainer>
              <Typography variant="body2">Storage Usage</Typography>
              <LinearProgress variant="determinate" value={storagePercentage} />
              <Typography variant="body2">
                {storageUsage.toFixed(2)} GB / {storageLimit.toFixed(2)} GB
              </Typography>
            </StorageUsageContainer>
          )}
          <Divider />
          <ListItem button component={Link} to="/settings">
            <ListItemIcon><Settings /></ListItemIcon>
            <ListItemText primary="Settings" />
          </ListItem>
          <ListItem button onClick={handleLogout}>
            <ListItemIcon><Logout /></ListItemIcon>
            <ListItemText primary="Log out" />
          </ListItem>
        </List>
      </SidebarContainer>
    </ThemeProvider>
  );
};

export default Sidebar;
